import type { ReactNode } from 'react';
import { maskNric, type ChinaPaymentRecord, type ChinaPaymentRequestAccount } from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { Icon, Sheet } from '@mypoz/ui';

import { useChinaPaymentDetail } from '@/features/china-payment/useChinaPaymentRecords.js';
import { useFormat } from '@/lib/format.js';
import { buttonClass } from '@/lib/styles.js';
import { RecordStatus, RecordType } from './RecordStatus.js';

export interface RequestDetailsSheetProps {
  /** The request's `id`, from `?request=`. */
  id: string;
  onClose: () => void;
}

/**
 * One China Payment request in full (MYP-63) — the old website's ✎ → 充值详情 page: the request
 * itself, then the details the customer filled in on it.
 *
 * Read from `alipaydetail` when the sheet opens rather than taken from the list row, so a link with
 * `?request=` works for a request on another page and the status is the latest. Only the details
 * that were filled in are shown (the old page prints every column, mostly blank), and the ID number
 * is masked as on the profile page. Never shown: the Taobao/Alipay account and password, and the
 * uploaded slip (the old page doesn't show it either).
 */
export function RequestDetailsSheet({ id, onClose }: RequestDetailsSheetProps) {
  const { t } = useTranslation();
  const { status, detail, retry } = useChinaPaymentDetail(id);

  return (
    <Sheet
      title={t('chinaPayment.records.sheet.title')}
      closeLabel={t('chinaPayment.records.sheet.close')}
      onClose={onClose}
      focusKey={status}
      footer={
        <button type="button" onClick={onClose} className={buttonClass('secondary', 'w-full')}>
          {t('chinaPayment.records.sheet.close')}
        </button>
      }
    >
      {status === 'loading' ? (
        <p aria-busy="true" className="py-10 text-center text-sm text-muted">
          {t('chinaPayment.records.sheet.loading')}
        </p>
      ) : status === 'error' ? (
        <div className="grid justify-items-center gap-3 py-10">
          <p className="text-center text-sm text-ink">{t('chinaPayment.records.sheet.failed')}</p>
          <button type="button" onClick={retry} className={buttonClass('secondary')}>
            <Icon name="refresh" size={18} />
            {t('actions.retry')}
          </button>
        </div>
      ) : status === 'missing' || !detail ? (
        <p className="py-10 text-center text-sm text-muted">
          {t('chinaPayment.records.sheet.missing')}
        </p>
      ) : (
        <div className="grid gap-6">
          <RequestSection record={detail.record} />
          {detail.account ? <AccountSection account={detail.account} /> : null}
        </div>
      )}
    </Sheet>
  );
}

function RequestSection({ record }: { record: ChinaPaymentRecord }) {
  const { t } = useTranslation();
  const fmt = useFormat();

  return (
    <section className="grid gap-3">
      <h3 className="text-sm font-semibold text-ink">{t('chinaPayment.records.sheet.request')}</h3>
      <dl className="grid gap-2.5 text-sm">
        <Field label={t('chinaPayment.records.columns.status')}>
          <RecordStatus record={record} />
        </Field>
        {record.status === 'cancelled' ? (
          <Field label={t('chinaPayment.records.reasonTitle')}>
            <span className={record.cancelReason ? 'text-ink' : 'text-muted'}>
              {record.cancelReason ?? t('chinaPayment.records.noReason')}
            </span>
          </Field>
        ) : null}
        <Field label={t('chinaPayment.records.columns.reference')}>
          <span className="tabular-nums">{record.reference ?? '—'}</span>
        </Field>
        <Field label={t('chinaPayment.records.columns.time')}>
          <span className="tabular-nums">
            {record.submittedAt ? fmt.dateTime(record.submittedAt) : '—'}
          </span>
        </Field>
        <Field label={t('chinaPayment.records.columns.type')}>
          <RecordType record={record} />
        </Field>
        <Field label={t('chinaPayment.records.columns.bank')}>{record.bankName ?? '—'}</Field>
        <Field label={t('chinaPayment.records.columns.rate')}>
          <span className="tabular-nums">
            {record.rate ? fmt.number(record.rate.value, record.rate.decimals) : '—'}
          </span>
        </Field>
        <Field label={t('chinaPayment.records.columns.rmAmount')}>
          <span className="font-semibold tabular-nums">
            {record.rmCents === undefined ? '—' : fmt.money(record.rmCents, 'RM')}
          </span>
        </Field>
        <Field label={t('chinaPayment.records.columns.rmbAmount')}>
          <span className="font-semibold tabular-nums">
            {record.rmbCents === undefined ? '—' : fmt.money(record.rmbCents, 'RMB')}
          </span>
        </Field>
      </dl>
    </section>
  );
}

function AccountSection({ account }: { account: ChinaPaymentRequestAccount }) {
  const { t } = useTranslation();
  const rows: [string, string | undefined][] = [
    [t('chinaPayment.records.sheet.fields.name'), account.name],
    [
      t('chinaPayment.records.sheet.fields.idNumber'),
      account.idNumber ? maskNric(account.idNumber) : undefined,
    ],
    [t('chinaPayment.records.sheet.fields.accountName'), account.accountName],
    [t('chinaPayment.records.sheet.fields.cardNumber'), account.cardNumber],
    [t('chinaPayment.records.sheet.fields.bank'), account.bank],
    [t('chinaPayment.records.sheet.fields.branch'), account.branch],
    [t('chinaPayment.records.sheet.fields.sellerName'), account.sellerName],
    [t('chinaPayment.records.sheet.fields.remark'), account.remark],
  ];

  return (
    <section className="grid gap-3">
      <h3 className="text-sm font-semibold text-ink">
        {t('chinaPayment.records.sheet.yourDetails')}
      </h3>
      <dl className="grid gap-2.5 text-sm">
        {rows.map(([label, value]) =>
          value === undefined ? null : (
            <Field key={label} label={label}>
              <span className="break-words">{value}</span>
            </Field>
          ),
        )}
      </dl>
    </section>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[minmax(0,9rem)_1fr] items-baseline gap-3">
      <dt className="text-muted">{label}</dt>
      <dd className="min-w-0 text-ink">{children}</dd>
    </div>
  );
}
